import React, { useEffect, useRef, useCallback } from 'react';
import './YouTubePlayer.css';

/**
 * YouTubePlayer — the YouTube-specific media sub-player.
 *
 * Built directly on the official YouTube IFrame Player API (window.YT.Player)
 * rather than the react-youtube wrapper. The wrapper did not reliably pass
 * `controls` / `cc_load_policy` / `cc_lang_pref` through to the embed in this
 * app, so we construct the player ourselves. This gives us native controls,
 * declarative captions, and the JS API (playVideo/pauseVideo/seekTo/
 * getCurrentTime/getVideoData) that MediaPlayer's control bar and the
 * window.__hojaVideo* bridges depend on.
 *
 * Props:
 *   videoId        (string) — the video to play, or with playlistId the one to
 *                           start on (null starts at the playlist's first video)
 *   playlistId     (string) — play this playlist; the player's own next/prev
 *                           and auto-advance step through it
 *   collapsed      (bool) — toggles a CSS class only; the player is NOT
 *                           re-created, so playback survives collapse/expand.
 *   targetLanguage (string, e.g. 'de' | 'fr') — used for cc_lang_pref and the
 *                           caption-track fallback. Read at creation time via a
 *                           ref; changing it mid-video does not re-create the player.
 *   onReady(event)       — pass-through; event is shaped { target: player }.
 *   onStateChange(event) — pass-through; event.data (1 === playing) preserved.
 *   onPlayerRef(player)  — called with the raw YT player instance once ready.
 */

// Singleton loader for the YouTube IFrame API script. Ensures the script is
// injected only once even when multiple players mount/unmount.
let ytApiPromise = null;
function loadYouTubeApi() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') { try { prev(); } catch (e) { /* ignore */ } }
      resolve(window.YT);
    };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
  });
  return ytApiPromise;
}

function YouTubePlayer({ videoId, playlistId, collapsed, targetLanguage, onReady, onStateChange, onPlayerRef }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  // Read target language lazily so a mid-session language change doesn't force
  // a player re-create (edge case, acceptable for POC).
  const targetLangRef = useRef(targetLanguage);
  targetLangRef.current = targetLanguage;

  // Pick the target-language caption track when the captions module loads,
  // falling back to English if the target language is unavailable.
  // NOTE: auto-generated (ASR) captions are NOT exposed via this API — that's a
  // known YouTube limitation. When only auto-captions exist the tracklist is
  // empty here and the declarative cc_lang_pref still applies.
  const applyCaptionTrack = useCallback((player) => {
    try {
      let tracklist = [];
      try { tracklist = player.getOption('captions', 'tracklist') || []; } catch (e) { /* ignore */ }
      if (!tracklist || tracklist.length === 0) {
        try { tracklist = player.getOption('cc', 'tracklist') || []; } catch (e) { /* ignore */ }
      }
      const codes = (tracklist || []).map((t) => t.languageCode);
      let chosen = null;
      if (codes.includes(targetLangRef.current)) chosen = targetLangRef.current;
      else if (codes.includes('en')) chosen = 'en';
      if (chosen) {
        try { player.setOption('captions', 'track', { languageCode: chosen }); } catch (e) { /* ignore */ }
        try { player.setOption('cc', 'track', { languageCode: chosen }); } catch (e) { /* ignore */ }
      }
    } catch (e) {
      /* captions module not ready — ignore */
    }
  }, []);

  // Create the player once per videoId/playlistId (on mount / when they change).
  // The callback props (onReady/onStateChange/onPlayerRef) and applyCaptionTrack
  // are intentionally kept out of the dep array: including them would re-create
  // the player on every parent render, killing playback. They are read via
  // closure and are stable enough for this purpose.
  useEffect(() => {
    let cancelled = false;
    loadYouTubeApi().then((YT) => {
      if (cancelled || !containerRef.current) return;
      // Destroy any prior instance before creating a new one.
      if (playerRef.current && playerRef.current.destroy) {
        try { playerRef.current.destroy(); } catch (e) { /* ignore */ }
        playerRef.current = null;
      }
      const playlistVars = playlistId ? { listType: 'playlist', list: playlistId } : {};
      playerRef.current = new YT.Player(containerRef.current, {
        ...(videoId ? { videoId } : {}),
        width: '100%',
        height: '100%',
        playerVars: {
          ...playlistVars,
          rel: 0,
          controls: 1,          // native controls visible
          cc_load_policy: 1,    // force captions on
          cc_lang_pref: targetLangRef.current, // preferred caption language
          fs: 1,
          playsinline: 1,
        },
        events: {
          onReady: (event) => {
            if (onPlayerRef) onPlayerRef(event.target);
            if (onReady) onReady(event);
          },
          onStateChange: (event) => {
            if (onStateChange) onStateChange(event);
          },
          onApiChange: (event) => {
            applyCaptionTrack(event.target);
          },
        },
      });
    });
    return () => {
      cancelled = true;
      if (playerRef.current && playerRef.current.destroy) {
        try { playerRef.current.destroy(); } catch (e) { /* ignore */ }
        playerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, playlistId]);

  return (
    <div className={`youtube-player-wrap ${collapsed ? 'youtube-player-wrap--collapsed' : ''}`}>
      <div className="youtube-player-aspect">
        {/* YT.Player replaces this div with the iframe on creation. */}
        <div ref={containerRef} />
      </div>
    </div>
  );
}

export default YouTubePlayer;
